import {
  Form,
  Input,
  Card,
  Collapse,
  DatePicker,
  ConfigProvider,
  Flex,
  Select,
  Switch,
  Tag,
  Dropdown,
  Grid,
  Button,
  Row as AntRow,
  Col as AntCol,
} from "antd";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import {
  ExerciseEntry,
  Programmazione,
  Split,
} from "../../types/Programmazione";
import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Title from "antd/es/typography/Title";
import "./ProgrammazioneForm.css";
import dayjs from "dayjs";
import "dayjs/locale/it";
import locale from "antd/locale/it_IT";
import localeData from "dayjs/plugin/localeData";
import { capitalize } from "../../utils/stringUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faCopy, faGripVertical, faLayerGroup, faPaste, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../common/IconButton";
import EmptyStateCard from "../../common/EmptyStateCard";
import { useClipboard } from "../../contexts/clipboard";
import classNames from "classnames";
import ExerciseCard from "./ExerciseCard";
import { Option } from "../../types/generics";
import type { ProgEditorOptions } from "../../hooks/useEntityOptions";
import {
  DragDropContext,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import StrictModeDroppable from "../../common/StrictModeDroppable";
import { v4 } from "uuid";

dayjs.extend(localeData);
dayjs.locale("it");

type UserOption = { label: string; value: number };

type Props = {
  programmazione?: Programmazione;
  onSubmit?: (values: any) => void;
  onValuesChange?: (changedValues: any, allValues: Programmazione) => void;
  loading?: boolean;
  exercisesOptions?: Option[];
  /** Muscle-group options for the per-exercise selector + catalog filtering. */
  muscleGroupOptions?: Option[];
  assignableUsers?: UserOption[];
  loadingUsers?: boolean;
  /** UX-PROG/FE-4 (#16d3b07c) — surfaces variant (d) of the empty-state card
   *  on /programmazioni/create when no splits exist yet: same CTAs as (a)
   *  plus a helper-text tip about pasting from a copied Programmazione. */
  isCreateMode?: boolean;
  /** PROG-WEEK-PARAMS/FE-1 — single page-level catalog bundle propagated
   *  down to every ExerciseProgressionEditor so the per-card hook
   *  fanout (~30 redundant Entities.* fetches on a typical
   *  programmazione load) is avoided. */
  progEditorOptions?: ProgEditorOptions;
};

export default function ProgrammazioneForm({
  programmazione,
  onSubmit,
  onValuesChange,
  loading,
  exercisesOptions,
  muscleGroupOptions,
  assignableUsers,
  loadingUsers,
  isCreateMode,
  progEditorOptions,
}: Props) {
  const [form] = Form.useForm<Programmazione>();
  const [splits, setSplits] = useState<Split[]>([]);
  // UX-PROG/FE-5 (#ce4b854a) — mobile breakpoint detection. AntD ships
  // `Grid.useBreakpoint()`; `xs` (<576px) collapses the benign cluster into
  // a "…" Dropdown overflow per the spec.
  // PROG-UX/IMPL F3 — two-tier model: `isCompact` (<sm) for "absolutely
  // compact" decisions (Dropdown overflow); `isMobile` (<md) for general
  // stack/wrap decisions so landscape-mini phones don't over-stack at 568px.
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.sm;
  const isMobile = !screens.md;

  // UX-PROG/FE-2 (#6bb5eca7, §2) — collapsible meta card. Default open on
  // create; default closed on edit when meta is "valid" (name + scadenza set).
  const [metaOpen, setMetaOpen] = useState<boolean>(true);

  const [isInitialized, setIsInitialized] = useState(false);
  // NOTE: `collapsedSplits[index] === true` means EXPANDED (the variable name
  // is inverted historically; the JSX gates body display on `=== true`).
  const [collapsedSplits, setCollapsedSplits] = useState<Record<number, boolean>>({});
  // UX-PROG/FE-5 (#ce4b854a, §5.4) — last split the trainer touched (added
  // exercise, pasted, expanded). Used to drive default-expand on init: the
  // most recently touched split is expanded; the rest stay collapsed.
  const [lastTouchedSplitIndex, setLastTouchedSplitIndex] = useState<number | null>(null);
  const { copiedSplit, copySplit, copiedExercise, copyExercise } = useClipboard();
  const splitsRef = useRef(splits);
  splitsRef.current = splits;
  const programmazioneRef = useRef(programmazione);
  programmazioneRef.current = programmazione;
  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;

  const toggleSplitCollapse = useCallback((index: number) => {
    setCollapsedSplits((prev) => ({ ...prev, [index]: !prev[index] }));
    setLastTouchedSplitIndex(index);
  }, []);

  useEffect(() => {
    if (programmazione && !isInitialized) {
      const splitsData = programmazione.splits || [];
      setSplits(splitsData);
      // Antd's `Form<Programmazione>` derives a stricter recursive shape for
      // `setFieldsValue` that lags the runtime `Programmazione` type. Cast at
      // the form boundary; `setFieldsValue` is structurally permissive at
      // runtime. (Mirrors the cast the AJV-validated payloads use elsewhere.)
      // `scadenza` is persisted/loaded as an ISO string, but the AntD
      // DatePicker is controlled by a dayjs object. Seeding the field with a
      // raw string leaves the picker in an invalid state (it renders empty and
      // edits don't round-trip cleanly), which is why a changed scadenza wasn't
      // being saved. Convert to dayjs at the form boundary.
      form.setFieldsValue({
        ...programmazione,
        scadenza: programmazione.scadenza
          ? dayjs(programmazione.scadenza)
          : undefined,
      } as Parameters<typeof form.setFieldsValue>[0]);
      // UX-PROG/FE-5 (#ce4b854a, §5.4) — on create (no splits) nothing to
      // expand; on edit, default-expand the LAST split (most recently edited
      // proxy when we don't yet have explicit per-split touch timestamps).
      // Others stay collapsed with their header still showing name + count.
      if (splitsData.length > 0) {
        const lastIdx = splitsData.length - 1;
        setCollapsedSplits({ [lastIdx]: true });
        setLastTouchedSplitIndex(lastIdx);
      }
      // UX-PROG/FE-2 (#6bb5eca7, §2) — meta collapse default. Open on create
      // (no existing programmazione.id) or when meta is incomplete; closed
      // on edit when name + scadenza are both filled.
      const metaValid = !!programmazione.name && !!programmazione.scadenza;
      setMetaOpen(!programmazione.id || !metaValid);
      setIsInitialized(true);
    }
  }, [programmazione, form, isInitialized]);

  const notifyParent = useCallback(
    (updatedSplits: Split[]) => {
      const allValues = form.getFieldsValue();
      onValuesChange?.(null, {
        ...programmazione,
        ...allValues,
        splits: updatedSplits,
        nSplits: updatedSplits.length,
      });
    },
    [form, onValuesChange, programmazione]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      if (!destination) {
        return;
      }

      if (source.droppableId === "splits") {
        const items = Array.from(splits);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        form.setFieldValue("splits", items);
        setSplits(items);
        notifyParent(items);
        return;
      }

      if (source.droppableId.startsWith("exercises-")) {
        const splitIndex = parseInt(source.droppableId.split("-")[1]);
        const currentSplits = [...splits];
        const split = currentSplits[splitIndex];
        const [reorderedExercise] = split.exercises.splice(source.index, 1);
        split.exercises.splice(destination.index, 0, reorderedExercise);
        currentSplits[splitIndex] = split;
        setSplits(currentSplits);
        form.setFieldValue("splits", currentSplits);
        notifyParent(currentSplits);
      }
    },
    [splits, form, notifyParent]
  );

  const handleAddSplit = useCallback(() => {
    const newSplit: Split = {
      dayName: `Split ${splits.length + 1}`,
      exercises: [],
    };
    const updatedSplits = [...splits, newSplit];
    setSplits(updatedSplits);

    form.setFieldValue("splits", updatedSplits);
    form.setFieldValue("nSplits", updatedSplits.length);
    notifyParent(updatedSplits);
    // FE-5 (#ce4b854a) — newly created split becomes the touched one; expand.
    const newIdx = updatedSplits.length - 1;
    setCollapsedSplits((prev) => ({ ...prev, [newIdx]: true }));
    setLastTouchedSplitIndex(newIdx);
  }, [splits, form, notifyParent]);

  const handleOverwriteSplit = useCallback(
    (splitIndex: number) => () => {
      if (!copiedSplit) return;
      const cloned: Split = JSON.parse(JSON.stringify(copiedSplit));
      const updatedSplits = [...splits];
      updatedSplits[splitIndex] = cloned;
      setSplits(updatedSplits);
      form.setFieldValue("splits", updatedSplits);
      notifyParent(updatedSplits);
    },
    [copiedSplit, splits, form, notifyParent]
  );

  const handleOverwriteExercise = useCallback(
    (splitIndex: number, exerciseIndex: number) => {
      if (!copiedExercise) return;
      const cloned: ExerciseEntry = JSON.parse(JSON.stringify(copiedExercise));
      cloned.id = v4();
      const updatedSplits = [...splits];
      updatedSplits[splitIndex] = {
        ...updatedSplits[splitIndex],
        exercises: updatedSplits[splitIndex].exercises.map((ex, idx) =>
          idx === exerciseIndex ? cloned : ex
        ),
      };
      setSplits(updatedSplits);
      form.setFieldValue("splits", updatedSplits);
      notifyParent(updatedSplits);
    },
    [copiedExercise, splits, form, notifyParent]
  );

  const onFinish = (values: Programmazione) => {
    onSubmit?.({ ...values, nSplits: splits.length });
  };

  const handleFormValuesChange = useCallback(
    (changedValues: any, allValues: Programmazione) => {
      // Merge form values con lo stato locale degli splits per preservare sets e weeks
      const mergedValues = {
        ...programmazione,
        ...allValues,
        nSplits: splits.length,
        splits: splits.map((localSplit, idx) => {
          const formSplit = allValues.splits?.[idx];
          if (!formSplit) return localSplit;

          return {
            ...formSplit,
            exercises: localSplit.exercises.map((localExercise, exIdx) => {
              const formExercise = formSplit.exercises?.[exIdx];
              if (!formExercise) return localExercise;
              return { ...formExercise };
            }),
          };
        }),
      };

      onValuesChange?.(changedValues, mergedValues);
    },
    [splits, onValuesChange, programmazione]
  );

  const handleAddExercise = useCallback(
    (splitIndex: number) => () => {
      const updatedSplits = [...splits];

      updatedSplits[splitIndex] = {
        ...updatedSplits[splitIndex],
        exercises: [
          ...(updatedSplits[splitIndex].exercises || []),
          {
            id: v4(),
            customName: "",
            type: "custom",
          },
        ],
      };
      setSplits(updatedSplits);
      form.setFieldValue("splits", updatedSplits);
      notifyParent(updatedSplits);
    },
    [splits, form, notifyParent]
  );

  const handleDeleteExercise = useCallback(
    (splitIndex: number, exerciseIndex: number) => {
      const updatedSplits = [...splits];
      updatedSplits[splitIndex].exercises.splice(exerciseIndex, 1);
      setSplits(updatedSplits);
      form.setFieldValue("splits", updatedSplits);
      notifyParent(updatedSplits);
    },
    [splits, form, notifyParent]
  );

  const handleDeleteSplit = useCallback(
    (splitIndex: number) => () => {
      const updatedSplits = [...splits];
      updatedSplits.splice(splitIndex, 1);
      setSplits(updatedSplits);
  
      form.setFieldValue("splits", updatedSplits);
      form.setFieldValue("nSplits", updatedSplits.length);
      notifyParent(updatedSplits);
    },
    [form, splits, notifyParent]
  );

  const handlePasteSplit = useCallback(() => {
    if (!copiedSplit) return;
    const cloned: Split = JSON.parse(JSON.stringify(copiedSplit));
    cloned.dayName = `${cloned.dayName} (copia)`;
    const updatedSplits = [...splits, cloned];
    setSplits(updatedSplits);

    form.setFieldValue("splits", updatedSplits);
    form.setFieldValue("nSplits", updatedSplits.length);
    notifyParent(updatedSplits);
  }, [copiedSplit, splits, form, notifyParent]);

  const handlePasteExercise = useCallback(
    (splitIndex: number) => () => {
      if (!copiedExercise) return;
      const cloned: ExerciseEntry = JSON.parse(JSON.stringify(copiedExercise));
      cloned.id = v4();
      const updatedSplits = [...splits];
      updatedSplits[splitIndex] = {
        ...updatedSplits[splitIndex],
        exercises: [...(updatedSplits[splitIndex].exercises || []), cloned],
      };
      setSplits(updatedSplits);
      form.setFieldValue("splits", updatedSplits);
      notifyParent(updatedSplits);
    },
    [copiedExercise, splits, form, notifyParent]
  );

  const handleFieldChange = useCallback(
    (
      splitIndex: number,
      exerciseIndex: number,
      field: keyof ExerciseEntry,
      value: any
    ) => {
      const splitsToUpdate = [...splitsRef.current];
      const exercise = splitsToUpdate[splitIndex].exercises[exerciseIndex];
      const updatedExercise = {
        ...exercise,
        [field]: value,
      };
      if (field === "type" && value === "custom") {
        updatedExercise.customName = "";
        updatedExercise.exerciseId = undefined;
      }
      splitsToUpdate[splitIndex].exercises[exerciseIndex] = updatedExercise;

      form.setFieldValue("splits", splitsToUpdate);
      onValuesChangeRef.current?.(null, {
        ...programmazioneRef.current,
        ...form.getFieldsValue(),
        splits: splitsToUpdate,
        nSplits: splitsToUpdate.length,
      });
      setSplits(splitsToUpdate);
    },
    [form]
  );

  const handleSplitFieldChange = useCallback(
    (field: keyof Split, splitIndex: number) => (evt: any) => {
      const splitsToUpdate = [...splits];
      const val = evt && evt.target ? evt.target.value : evt;
      const split = splitsToUpdate[splitIndex];
      const updatedSplit = {
        ...split,
        [field]: val,
      };
      splitsToUpdate[splitIndex] = updatedSplit;
      setSplits(splitsToUpdate);
      form.setFieldValue("splits", splitsToUpdate);
      notifyParent(splitsToUpdate);
    },
    [splits, form, notifyParent]
  );

  const renderAsyncSplitsContent = useCallback(() => {
    if (splits.length === 0)
      return (
        <div className="mt-4">
          <EmptyStateCard
            icon={faLayerGroup}
            title="Nessuno Split"
            body="Aggiungi il primo Split per iniziare a costruire la programmazione."
            helperText={
              isCreateMode
                ? "Tip: puoi anche incollare gli Split da un'altra Programmazione che hai copiato."
                : undefined
            }
            primaryCta={{
              label: "Aggiungi Split",
              onClick: handleAddSplit,
              icon: faPlus,
            }}
            secondaryCta={
              copiedSplit
                ? { label: "Incolla Split", onClick: handlePasteSplit, icon: faPaste }
                : undefined
            }
          />
        </div>
      );

    return (
      <>
        {/* UX-PROG/FE-2 (#6bb5eca7, §5.2) — Splits workspace header. Replaces
            the meta-card "Aggiungi Split" strip. Houses the Title, count
            Tag, primary "Aggiungi Split", and secondary "Incolla Split" (when
            a split is on the clipboard). */}
        <Flex
          align="center"
          gap={12}
          wrap="wrap"
          className="my-4"
          justify="space-between"
        >
          <Flex align="center" gap={12} wrap="wrap">
            <Title level={3} className="m-0">
              Splits
            </Title>
            <Tag color="blue" style={{ margin: 0 }}>
              {splits.length} {splits.length === 1 ? "split" : "split"}
            </Tag>
          </Flex>
          <Flex
            align="center"
            gap={8}
            wrap="wrap"
            vertical={isCompact}
            style={isCompact ? { width: "100%" } : undefined}
          >
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddSplit}
              block={isCompact}
            >
              Aggiungi Split
            </Button>
            {copiedSplit && (
              <Button
                icon={<FontAwesomeIcon icon={faPaste} />}
                onClick={handlePasteSplit}
                block={isCompact}
              >
                Incolla Split
              </Button>
            )}
          </Flex>
        </Flex>
        {/* UX-PROG/FE-2 (#6bb5eca7, §5.3) — Splits index chip row. Visible
            ≥sm when there is more than one split; each chip smooth-scrolls
            to its Split card. */}
        {!isMobile && splits.length > 1 && (
          <Flex
            gap={6}
            wrap="wrap"
            className="mb-3"
            style={{
              paddingTop: 8,
              borderTop: "1px dashed var(--app-gray-100)",
            }}
          >
            {splits.map((s, idx) => (
              <Tag
                key={`splits-index-${idx}`}
                color="blue"
                style={{ cursor: "pointer", margin: 0 }}
                onClick={() => {
                  const el = document.getElementById(`split-card-${idx}`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {s.dayName || `Split ${idx + 1}`} · {s.exercises?.length || 0} es.
              </Tag>
            ))}
          </Flex>
        )}
        <StrictModeDroppable droppableId="splits" type="split">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              {splits.map((split, index) => (
                <Draggable
                  key={`split-${index}`}
                  draggableId={`split-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <div
                      id={`split-card-${index}`}
                      key={index}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{ ...provided.draggableProps.style, scrollMarginTop: 96 }}
                    >
                      <Card className="bg-card rounded-lg shadow-sm border progression-card mb-0">
                        <Flex
                          justify="space-between"
                          align={isCompact ? "stretch" : "center"}
                          vertical={isCompact}
                          gap={isCompact ? 8 : 12}
                          className="my-2 mb-2"
                        >
                          <Flex align="center" gap="small" style={{ flex: 1, width: "100%" }}>
                            <span
                              {...provided.dragHandleProps}
                              style={{
                                cursor: "grab",
                                color: "var(--app-gray-400)",
                                fontSize: 13,
                                padding: "8px 4px",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <FontAwesomeIcon icon={faGripVertical} />
                            </span>
                            <IconButton
                              onClick={(e) => { e.preventDefault(); toggleSplitCollapse(index); }}
                            >
                              <FontAwesomeIcon
                                icon={collapsedSplits[index] ? faChevronDown : faChevronRight}
                                className="cursor-pointer"
                                style={{ fontSize: 14 }}
                              />
                            </IconButton>
                            <Form.Item
                              noStyle
                              name={["splits", index, "dayName"]}
                              initialValue={split.dayName}
                            >
                              <Input
                                size={isCompact ? "middle" : "large"}
                                onChange={handleSplitFieldChange(
                                  "dayName",
                                  index
                                )}
                                // PROG-DETAIL/IMPL N3 — defensively isolate
                                // input clicks from the neighbouring chevron
                                // IconButton, since on <sm the input shrinks
                                // and adjacent taps can misfire.
                                onClick={(e) => e.stopPropagation()}
                                className="custom-input"
                                placeholder={`Nome Split ${index + 1}...`}
                              />
                            </Form.Item>
                          </Flex>
                          <Flex
                            align="center"
                            gap={12}
                            justify={isCompact ? "flex-end" : undefined}
                          >
                            <Tag color="blue" style={{ borderRadius: "12px", margin: 0 }}>
                              {split.exercises?.length || 0} es.
                            </Tag>
                            {/* FE-5 (#ce4b854a) — benign cluster: Copia +
                                Incolla. Desktop: inline icons. Compact (<sm):
                                collapsed behind a "…" Dropdown. */}
                            {isCompact ? (
                              <Dropdown
                                trigger={["click"]}
                                menu={{
                                  items: [
                                    {
                                      key: "copia",
                                      label: "Copia Split",
                                      icon: <FontAwesomeIcon icon={faCopy} />,
                                      onClick: () => copySplit(split),
                                    },
                                    ...(copiedSplit
                                      ? [{
                                          key: "incolla",
                                          label: "Incolla qui (sovrascrive)",
                                          icon: <FontAwesomeIcon icon={faPaste} />,
                                          onClick: () => handleOverwriteSplit(index)(),
                                        }]
                                      : []),
                                  ],
                                }}
                              >
                                <IconButton tooltip="Azioni">
                                  <FontAwesomeIcon icon={faEllipsis} className="cursor-pointer" />
                                </IconButton>
                              </Dropdown>
                            ) : (
                              <Flex align="center" gap={4}>
                                <IconButton
                                  tooltip="Copia Split"
                                  onClick={() => copySplit(split)}
                                >
                                  <FontAwesomeIcon icon={faCopy} className="cursor-pointer" />
                                </IconButton>
                                {copiedSplit && (
                                  <IconButton
                                    tooltip="Incolla qui (sovrascrive)"
                                    onClick={handleOverwriteSplit(index)}
                                  >
                                    <FontAwesomeIcon icon={faPaste} className="cursor-pointer" />
                                  </IconButton>
                                )}
                              </Flex>
                            )}
                            {/* FE-5 (#ce4b854a) — destructive cluster.
                                Separated by the Flex gap={12} from the benign
                                cluster. Always visible, never collapsed. */}
                            <IconButton
                              tooltip="Elimina Split"
                              onClick={handleDeleteSplit(index)}
                            >
                              <FontAwesomeIcon
                                icon={faXmark}
                                color="red"
                                className="cursor-pointer"
                              />
                            </IconButton>
                          </Flex>
                        </Flex>
                        {/* FE-5 (#ce4b854a, §5.4) — collapsed-Split summary
                            line: first 3 exercise names joined with "·", plus
                            "+N altri" chip when more. Only visible when the
                            split body is collapsed. */}
                        {!collapsedSplits[index] && split.exercises.length > 0 && (() => {
                          const names = split.exercises
                            .map((ex) => (ex.type === "catalog"
                              ? exercisesOptions?.find((o) => o.value.id === ex.exerciseId)?.label
                              : ex.customName) || `Esercizio`);
                          const head = names.slice(0, 3).join(" · ");
                          const rest = names.length - 3;
                          return (
                            <div
                              className="text-muted small mt-1"
                              style={{ paddingLeft: isCompact ? 0 : 40 }}
                            >
                              {head}
                              {rest > 0 && (
                                <Tag style={{ marginLeft: 6, marginRight: 0 }}>+{rest} altri</Tag>
                              )}
                            </div>
                          );
                        })()}
                        <div style={{ display: collapsedSplits[index] ? "block" : "none" }}>
                          <StrictModeDroppable
                            droppableId={`exercises-${index}`}
                            type="exercise"
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={classNames("border-container mb-2", {
                                  dashed: split.exercises.length === 0,
                                })}
                              >
                                {split.exercises.length === 0 ? (
                                  <div className="py-3">
                                    <EmptyStateCard
                                      title="Nessun esercizio in questo Split"
                                      body="Aggiungi un esercizio dal catalogo o creane uno personalizzato."
                                      primaryCta={{
                                        label: "Aggiungi Esercizio",
                                        onClick: handleAddExercise(index),
                                        icon: faPlus,
                                      }}
                                      secondaryCta={
                                        copiedExercise
                                          ? {
                                              label: "Incolla Esercizio",
                                              onClick: handlePasteExercise(index),
                                              icon: faPaste,
                                            }
                                          : undefined
                                      }
                                    />
                                    {provided.placeholder}
                                  </div>
                                ) : (
                                  <>
                                    <div className="mb-3">
                                      {split.exercises.map((exercise, exIndex) => (
                                        <ExerciseCard
                                          key={exIndex}
                                          exercise={exercise}
                                          index={exIndex}
                                          splitIndex={index}
                                          onDelete={handleDeleteExercise}
                                          exerciseCatalog={exercisesOptions}
                                          muscleGroupOptions={muscleGroupOptions}
                                          onFieldChange={handleFieldChange}
                                          onOverwrite={handleOverwriteExercise}
                                          isEditMode={!!programmazione?.id}
                                          progEditorOptions={progEditorOptions}
                                        />
                                      ))}
                                      {provided.placeholder}
                                    </div>
                                    <Flex gap={8} vertical={isCompact}>
                                      <button
                                        className="add-exercise-btn"
                                        style={{ flex: 1 }}
                                        onClick={handleAddExercise(index)}
                                      >
                                        <span>
                                          <FontAwesomeIcon
                                            icon={faPlus}
                                            className="me-2"
                                          />
                                          Aggiungi Esercizio
                                        </span>
                                      </button>
                                      {copiedExercise && (
                                        <button
                                          className="add-exercise-btn"
                                          style={{ flex: 1 }}
                                          onClick={handlePasteExercise(index)}
                                        >
                                          <span>
                                            <FontAwesomeIcon
                                              icon={faPaste}
                                              className="me-2"
                                            />
                                            Incolla Esercizio
                                          </span>
                                        </button>
                                      )}
                                    </Flex>
                                  </>
                                )}
                              </div>
                            )}
                          </StrictModeDroppable>
                        </div>
                      </Card>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </>
    );
  }, [
    splits,
    handleFieldChange,
    handleSplitFieldChange,
    exercisesOptions,
    handleAddExercise,
    handleDeleteExercise,
    handleDeleteSplit,
    copiedSplit,
    handleAddSplit,
    handlePasteSplit,
    handleOverwriteSplit,
    copiedExercise,
    handlePasteExercise,
    handleOverwriteExercise,
    collapsedSplits,
    toggleSplitCollapse,
    isCreateMode,
    isMobile,
    progEditorOptions,
  ]);

  return (
    <Container fluid="lg" className="mt-3">
      <Form form={form} layout="vertical" onFinish={onFinish} onValuesChange={handleFormValuesChange}>
        {/* UX-PROG/FE-2 (#6bb5eca7, §2) — collapsible "Informazioni Generali"
            card. Single Collapse item; header shows a compact summary when
            collapsed (name · scade DATE · assignee, plus a Pubblica chip). */}
        <MetaCardCollapse
          form={form}
          open={metaOpen}
          onChange={setMetaOpen}
          assignableUsers={assignableUsers}
          loadingUsers={loadingUsers}
        />
        <DragDropContext onDragEnd={onDragEnd}>
          {renderAsyncSplitsContent()}
        </DragDropContext>
      </Form>
    </Container>
  );
}

/**
 * UX-PROG/FE-2 (#6bb5eca7, §2) — collapsible meta-card subcomponent. Held
 * separate so the Collapse's header can subscribe to live form values via
 * `Form.useWatch` (which would otherwise re-render the whole parent on
 * every keystroke).
 *
 * Reordered field flow per spec: Nome → Scadenza → Assegnato a → Pubblica.
 */
function MetaCardCollapse({
  form,
  open,
  onChange,
  assignableUsers,
  loadingUsers,
}: {
  form: any;
  open: boolean;
  onChange: (next: boolean) => void;
  assignableUsers?: UserOption[];
  loadingUsers?: boolean;
}) {
  const watchedName = Form.useWatch("name", form);
  const watchedScadenza = Form.useWatch("scadenza", form);
  const watchedUserId = Form.useWatch("userId", form);
  const watchedIsPublic = Form.useWatch("isPublic", form);
  const assigneeLabel =
    watchedUserId != null
      ? assignableUsers?.find((u) => u.value === watchedUserId)?.label
      : undefined;
  const scadenzaLabel = watchedScadenza
    ? capitalize(dayjs(watchedScadenza).format("D MMM YYYY"))
    : null;

  return (
    <Collapse
      activeKey={open ? ["meta"] : []}
      onChange={(keys) => onChange((keys as string[]).includes("meta"))}
      className="mb-3"
      bordered
      items={[
        {
          key: "meta",
          label: open ? (
            <Title level={3} className="m-0">
              Informazioni Generali
            </Title>
          ) : (
            <Flex align="center" gap={8} wrap="wrap">
              <strong>{watchedName || "Nuova Programmazione"}</strong>
              {scadenzaLabel && (
                <span className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                  · scade {scadenzaLabel}
                </span>
              )}
              <span className="text-muted small" style={{ whiteSpace: "nowrap" }}>
                · {assigneeLabel || "non assegnato"}
              </span>
              {watchedIsPublic && (
                <Tag color="green" style={{ margin: 0 }}>
                  Pubblica
                </Tag>
              )}
            </Flex>
          ),
          children: (
            <>
              <AntRow gutter={[16, 16]}>
                <AntCol xs={24} md={12} lg={8}>
                  <Form.Item
                    label={<strong>Nome Programmazione</strong>}
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Inserisci il nome della programmazione",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      size="large"
                      className="custom-input"
                      placeholder="Nome Programmazione..."
                    />
                  </Form.Item>
                </AntCol>
                <AntCol xs={24} md={12} lg={8}>
                  {/* ConfigProvider MUST wrap the Form.Item, not sit between
                      Form.Item and DatePicker: Form.Item injects value/onChange
                      into its first child element. If that child is
                      ConfigProvider, the DatePicker never receives them and the
                      picked date never reaches the form store (scadenza edits
                      were silently dropped). */}
                  <ConfigProvider locale={locale}>
                    <Form.Item
                      label={<strong>Scadenza</strong>}
                      name="scadenza"
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        size="large"
                        allowClear={false}
                        className="w-100 custom-input text-capitalize"
                        format={(value) =>
                          capitalize(value.format("dddd D MMMM YYYY"))
                        }
                        placeholder="gg/mm/aaaa"
                      />
                    </Form.Item>
                  </ConfigProvider>
                </AntCol>
                {assignableUsers && assignableUsers.length > 0 && (
                  <AntCol xs={24} md={12} lg={8}>
                    <Form.Item
                      label={<strong>Assegnato a</strong>}
                      name="userId"
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        size="large"
                        className="w-100 custom-input"
                        placeholder="Seleziona un utente..."
                        showSearch
                        allowClear
                        loading={loadingUsers}
                        options={assignableUsers}
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </AntCol>
                )}
              </AntRow>
              <Flex
                justify="space-between"
                align="center"
                className="mt-3 pt-3 border-top"
              >
                <div>
                  <strong>Programmazione Pubblica</strong>
                  <p className="text-secondary mb-0">
                    Rendi visibile questa programmazione ad altri utenti
                  </p>
                </div>
                <Form.Item name="isPublic" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </Flex>
            </>
          ),
        },
      ]}
    />
  );
}
